/**
 * IntelligentRunTranscript - Unified intelligent response experience
 *
 * This component combines the ThinkingProcessCard and IntelligentResponseCard
 * to provide a premium, Bentley-level chat experience.
 *
 * Architecture:
 * - While processing: Shows ThinkingProcessCard with live step updates
 * - On completion: Shows IntelligentResponseCard as a SEPARATE card
 * - Both can be visible together for full context
 *
 * This replaces the old RunTranscriptMessage's inline answer display
 * with a proper two-card separation for premium UX.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiUrl } from '../../lib/apiBase.js';
import {
    ThinkingProcessCard,
    type ProcessStep,
    type ThinkingProcessData,
} from './ThinkingProcessCard.js';
import {
    IntelligentResponseCard,
    type IntelligentResponseData,
} from './IntelligentResponseCard.js';
import type {
    AgenticContext,
    MetaBuildResult,
    StudioRepoContext,
} from '../../stores/useChatStore.js';

export interface RunStepEvent {
    type: string;
    traceId: string;
    seq: number;
    ts: string;
    stage?: string;
    action?: string;
    message: string;
    data?: Record<string, unknown>;
}

export interface IntelligentRunData {
    traceId: string;
    status: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'BLOCKED' | 'INVALID_SUCCESS';
    mode: string;
    startedAt: string;
    steps: RunStepEvent[];
    answer?: string;
    artifacts?: string[];
    failureReason?: string;
    verdict?: 'SUCCESS' | 'FAILED' | 'INCOMPLETE' | 'BLOCKED' | 'DENIED' | 'NOT_APPLICABLE';
    reasonCode?: string;
    /** Intent classification */
    intentName?: string;
    intentConfidence?: number;
    /** Verification results */
    verified?: boolean;
    verifier?: string;
    verificationScore?: number;
    /** Response metadata */
    confidence?: number;
    model?: string;
    responseTimeMs?: number;
}

interface IntelligentRunTranscriptProps {
    traceId: string;
    initialData?: IntelligentRunData;
    streamToken?: string;
    className?: string;
    /** Decision + context exposure (P0 Devin UX) */
    headerMeta?: {
        intentClass?: string;
        intentName?: string;
        runMode?: boolean;
        context?: AgenticContext | null;
        studioContext?: StudioRepoContext | null;
        metaBuild?: MetaBuildResult | null;
    };
    /** Callback when answer is received - allows parent to handle separately */
    onAnswerReceived?: (answer: string, metadata: IntelligentResponseData) => void;
    /** If true, don't render the answer card here (parent will render it separately) */
    externalAnswerRender?: boolean;
    /** Show compact process view */
    compact?: boolean;
}

/**
 * Convert RunStepEvent to ProcessStep for ThinkingProcessCard
 */
const toProcessStep = (
    event: RunStepEvent,
    status: 'complete' | 'active' | 'pending'
): ProcessStep => ({
    id: `step-${event.seq}`,
    stage: event.stage || '',
    action: event.action || '',
    message: event.message,
    timestamp: event.ts,
    status,
    metadata: event.data
        ? {
              confidence: event.data.confidence as number | undefined,
              pass: event.data.pass as boolean | undefined,
              verifier: event.data.verifier as string | undefined,
              score: event.data.score as number | undefined,
          }
        : undefined,
});

export const IntelligentRunTranscript: React.FC<IntelligentRunTranscriptProps> = ({
    traceId,
    initialData,
    streamToken,
    className = '',
    headerMeta,
    onAnswerReceived,
    externalAnswerRender = false,
    compact = false,
}) => {
    const [data, setData] = useState<IntelligentRunData>(() => {
        if (initialData) return initialData;
        return {
            traceId,
            status: 'RUNNING',
            mode: 'SOMA_DIRECT',
            startedAt: new Date().toISOString(),
            steps: [],
        };
    });

    const [reconciled, setReconciled] = useState(false);
    const eventSourceRef = useRef<EventSource | null>(null);
    const isTerminalRef = useRef(false);
    const answerEmittedRef = useRef(false);
    const reconcileAttemptedRef = useRef(false);

    const reconcileToTruth = useCallback(async (): Promise<void> => {
        if (reconcileAttemptedRef.current) return;
        reconcileAttemptedRef.current = true;

        try {
            const baseFinal = apiUrl(`/api/runs/${encodeURIComponent(traceId)}/final`);
            const baseRunlog = apiUrl(`/api/runs/${encodeURIComponent(traceId)}/runlog`);
            const baseResponse = apiUrl(`/api/runs/${encodeURIComponent(traceId)}/response`);
            const baseArtifacts = apiUrl(`/api/runs/${encodeURIComponent(traceId)}/artifacts`);

            const [finalRes, runlogRes, responseRes, artifactsRes] = await Promise.all([
                fetch(baseFinal),
                fetch(baseRunlog),
                fetch(baseResponse),
                fetch(baseArtifacts),
            ]);

            if (!finalRes.ok) return; // 404 => no run record yet

            const finalJson = (await finalRes.json()) as {
                traceId: string;
                status?: string;
                executionEnvelope?: Record<string, unknown>;
            };

            const runlogJson = runlogRes.ok ? ((await runlogRes.json()) as RunStepEvent[]) : [];
            const responseText = responseRes.ok ? await responseRes.text() : undefined;
            const artifactsJson = artifactsRes.ok
                ? ((await artifactsRes.json()) as { artifactPointers?: unknown; traceId?: string })
                : null;
            const artifactsList = (() => {
                const pointers = (artifactsJson as any)?.artifactPointers;
                if (Array.isArray(pointers)) {
                    return pointers.filter((p: unknown) => typeof p === 'string') as string[];
                }
                if (pointers && typeof pointers === 'object') {
                    return Object.values(pointers).filter(
                        (p: unknown) => typeof p === 'string'
                    ) as string[];
                }
                return undefined;
            })();

            setData(prev => {
                const nextStatus = (() => {
                    const s = String(finalJson.status || '').toUpperCase();
                    if (
                        s === 'SUCCESS' ||
                        s === 'FAILED' ||
                        s === 'BLOCKED' ||
                        s === 'INVALID_SUCCESS'
                    ) {
                        return s as IntelligentRunData['status'];
                    }
                    return prev.status;
                })();

                const envelope = finalJson.executionEnvelope as
                    | {
                          verdict?: IntelligentRunData['verdict'];
                          reasonCode?: string;
                      }
                    | undefined;

                return {
                    ...prev,
                    status: nextStatus,
                    steps: Array.isArray(runlogJson) ? runlogJson : prev.steps,
                    answer: responseText ?? prev.answer,
                    artifacts: artifactsList ?? prev.artifacts,
                    verdict: envelope?.verdict ?? prev.verdict,
                    reasonCode: envelope?.reasonCode ?? prev.reasonCode,
                };
            });

            setReconciled(true);
            isTerminalRef.current = true;
        } catch (error) {
            // Non-fatal: reconciliation is best-effort
            console.warn('[IntelligentRunTranscript] Reconciliation failed:', error);
        }
    }, [traceId]);

    // Extract answer metadata for IntelligentResponseCard
    const buildAnswerData = useCallback((): IntelligentResponseData | null => {
        if (!data.answer) return null;

        const derivedVerdict: IntelligentResponseData['verdict'] = data.verdict
            ? data.verdict
            : data.status === 'SUCCESS'
              ? 'SUCCESS'
              : data.status === 'BLOCKED'
                ? 'BLOCKED'
                : data.status === 'FAILED' || data.status === 'INVALID_SUCCESS'
                  ? 'FAILED'
                  : 'INCOMPLETE';

        const hasVerificationArtifacts = Array.isArray(data.artifacts)
            ? data.artifacts.some(artifact => artifact.includes('verification'))
            : false;

        return {
            answer: data.answer,
            confidence: data.confidence ?? data.verificationScore ?? 0.85,
            model: data.model,
            traceId: data.traceId,
            verdict: derivedVerdict,
            hasVerificationArtifacts,
            reasonCode: data.reasonCode ?? data.failureReason,
            verified: data.verified,
            verifier: data.verifier,
            verificationScore: data.verificationScore,
            responseTimeMs: data.responseTimeMs,
            intentName: data.intentName,
            timestamp: new Date(data.startedAt),
            reasoningSummary: data.steps
                .filter(s => s.message && !s.message.toLowerCase().includes('answer'))
                .slice(0, 5)
                .map(s => s.message),
        };
    }, [data]);

    // Emit answer when received
    useEffect(() => {
        if (data.answer && !answerEmittedRef.current && onAnswerReceived) {
            const answerData = buildAnswerData();
            if (answerData) {
                onAnswerReceived(data.answer, answerData);
                answerEmittedRef.current = true;
            }
        }
    }, [data.answer, onAnswerReceived, buildAnswerData]);

    // SSE connection for live updates
    useEffect(() => {
        // Don't connect if we already have terminal status
        if (
            initialData?.status &&
            ['SUCCESS', 'FAILED', 'BLOCKED', 'INVALID_SUCCESS'].includes(initialData.status)
        ) {
            isTerminalRef.current = true;
            return;
        }

        if (!streamToken) {
            // If we cannot connect to SSE (e.g., after refresh), attempt reconciliation.
            void reconcileToTruth();
            return;
        }

        const url = apiUrl(
            `/api/soma/run/${encodeURIComponent(traceId)}/stream?token=${encodeURIComponent(streamToken)}`
        );
        const es = new EventSource(url);
        eventSourceRef.current = es;

        es.onmessage = event => {
            try {
                const stepEvent = JSON.parse(event.data) as RunStepEvent;

                setData(prev => {
                    const exists = prev.steps.some(s => s.seq === stepEvent.seq);
                    if (exists) return prev;

                    const newSteps = [...prev.steps, stepEvent];
                    let newStatus = prev.status;
                    let newAnswer = prev.answer;
                    let newFailureReason = prev.failureReason;
                    let verified = prev.verified;
                    let verifier = prev.verifier;
                    let verificationScore = prev.verificationScore;
                    let confidence = prev.confidence;
                    let verdict = prev.verdict;

                    // Handle verification events
                    if (stepEvent.type === 'verification') {
                        verified = (stepEvent.data as any)?.pass ?? false;
                        verifier = (stepEvent.data as any)?.verifier;
                        verificationScore = (stepEvent.data as any)?.score;
                    }

                    // Handle completion
                    if (stepEvent.type === 'run_completed') {
                        const success = (stepEvent.data as any)?.success;
                        if (
                            success === false &&
                            (stepEvent.data as any)?.reason === 'no_steps_or_output'
                        ) {
                            newStatus = 'INVALID_SUCCESS';
                            verdict = 'FAILED';
                        } else if (success) {
                            newStatus = 'SUCCESS';
                            verdict = 'SUCCESS';
                        } else {
                            newStatus = 'FAILED';
                            newFailureReason = (stepEvent.data as any)?.reason;
                            verdict = 'FAILED';
                        }

                        // Extract answer
                        if (stepEvent.message.startsWith('Answer:')) {
                            newAnswer = stepEvent.message.replace('Answer:', '').trim();
                        }

                        // Get confidence from completion data
                        confidence = (stepEvent.data as any)?.confidence ?? confidence;

                        isTerminalRef.current = true;
                    } else if (stepEvent.type === 'run_blocked') {
                        newStatus = 'BLOCKED';
                        newFailureReason = (stepEvent.data as any)?.status;
                        verdict = 'BLOCKED';
                        isTerminalRef.current = true;
                    }

                    return {
                        ...prev,
                        steps: newSteps,
                        status: newStatus,
                        answer: newAnswer,
                        failureReason: newFailureReason,
                        verdict,
                        verified,
                        verifier,
                        verificationScore,
                        confidence,
                    };
                });

                if (stepEvent.type === 'run_completed' || stepEvent.type === 'run_blocked') {
                    es.close();
                }
            } catch {
                // Ignore malformed events
            }
        };

        es.onerror = () => {
            if (!isTerminalRef.current) {
                es.close();
                void reconcileToTruth();
            }
        };

        return () => {
            es.close();
        };
    }, [traceId, streamToken, initialData?.status, reconcileToTruth]);

    // Convert to ThinkingProcessCard format
    const thinkingData: ThinkingProcessData = {
        traceId: data.traceId,
        status: data.status === 'INVALID_SUCCESS' ? 'FAILED' : data.status,
        mode: data.mode,
        startedAt: data.startedAt,
        steps: data.steps.map((step, idx) =>
            toProcessStep(
                step,
                idx === data.steps.length - 1 && data.status === 'RUNNING' ? 'active' : 'complete'
            )
        ),
        intent: data.intentName
            ? {
                  name: data.intentName,
                  confidence: data.intentConfidence ?? 0.85,
              }
            : undefined,
    };

    const answerData = buildAnswerData();
    const isComplete = ['SUCCESS', 'FAILED', 'BLOCKED', 'INVALID_SUCCESS'].includes(data.status);
    const showAnswer = answerData && isComplete && !externalAnswerRender;

    return (
        <div className={`space-y-4 ${className}`}>
            {/* P0: Decision visibility + context used (always visible when available) */}
            {(reconciled ||
                headerMeta?.intentName ||
                headerMeta?.studioContext?.repo ||
                headerMeta?.context?.activeFile ||
                headerMeta?.metaBuild?.specPath) && (
                <div className="px-4 py-3 rounded-xl border border-slate-700/40 bg-slate-900/40">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
                        {reconciled && (
                            <span className="px-2 py-0.5 rounded-full border border-emerald-600/30 bg-emerald-600/10 text-emerald-200">
                                Reconciled
                            </span>
                        )}
                        {headerMeta?.intentName && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Intent: {headerMeta.intentName}
                            </span>
                        )}
                        {headerMeta?.runMode !== undefined && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Run: {headerMeta.runMode ? 'traced' : 'chat'}
                            </span>
                        )}
                        {headerMeta?.metaBuild &&
                            typeof headerMeta.metaBuild.dryRun === 'boolean' && (
                                <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                    MetaBuild: {headerMeta.metaBuild.dryRun ? 'dry-run' : 'apply'}
                                </span>
                            )}
                        {headerMeta?.studioContext?.repo && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40 font-mono">
                                {headerMeta.studioContext.repo}
                                {headerMeta.studioContext.branch
                                    ? `@${headerMeta.studioContext.branch}`
                                    : ''}
                            </span>
                        )}
                        {headerMeta?.metaBuild?.specPath && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40 font-mono">
                                Spec: {String(headerMeta.metaBuild.specPath)}
                            </span>
                        )}
                        {headerMeta?.context?.activeFile && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40 font-mono">
                                File: {headerMeta.context.activeFile}
                            </span>
                        )}
                        {headerMeta?.context?.activePanel && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Panel: {headerMeta.context.activePanel}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Process/Thinking Card - Always show while running or for context */}
            <ThinkingProcessCard data={thinkingData} compact={compact} />

            {/* Intelligent Response Card - Separate premium answer display */}
            <AnimatePresence>
                {showAnswer && answerData && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <IntelligentResponseCard data={answerData} />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Failure Display */}
            {data.status === 'FAILED' && data.failureReason && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 rounded-xl border border-red-500/30 bg-red-500/10"
                >
                    <div className="text-sm text-red-400">Failed: {data.failureReason}</div>
                </motion.div>
            )}

            {/* Invalid Success Warning */}
            {data.status === 'INVALID_SUCCESS' && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 rounded-xl border border-orange-500/30 bg-orange-500/10"
                >
                    <div className="text-xs text-orange-400">
                        Warning: Run completed without emitting steps or output.
                    </div>
                </motion.div>
            )}

            {/* Artifacts */}
            {data.artifacts && data.artifacts.length > 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="px-4 py-3 rounded-xl border border-slate-700/30 bg-slate-800/30"
                >
                    <div className="text-xs text-slate-400 mb-2">Artifacts</div>
                    <div className="space-y-1">
                        {data.artifacts.map((artifact, idx) => (
                            <div key={idx} className="text-xs text-slate-300 font-mono">
                                • {artifact}
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default IntelligentRunTranscript;
